// src/App.js
import React, { useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import GlobalErrorBoundary from "./features/common/GlobalErrorBoundary";
import ProtectedRoute from "./features/common/ProtectedRoute";
import { fetchTokenAsync } from "./features/common/FetchTokenSlice";

// Public pages
import HomePage from "./pages/CommonPublicPages/Home/HomePage";
import HomeLoggedIn from "./pages/CommonPublicPages/Home/HomeLoggedIn";
import { CompanyListPage } from "./pages/CommonPublicPages/CompanyPages/CompanyListPage";
import { CompanySinglePage } from "./pages/CommonPublicPages/CompanyPages/companySinglePage";
import { Notify } from "./pages/CommonPublicPages/Basic/Notify";
import { MyReviewsPage } from "./pages/CommonPublicPages/Basic/MyReviewsPage";
import SubscriptionBillingPage from "./pages/CommonPublicPages/Basic/Subscription&BillingPage";
import NotFound404 from "./pages/CommonPublicPages/Basic/NotFound404";
import AboutUs from "./features/Public/RawData/AboutUs";
import PrivacyPolicy from "./features/Public/RawData/PrivacyPolicy";
import RefundPolicy from "./features/Public/RawData/RefundPolicy";
import CoreValues from "./features/Public/RawData/CoreValues";
import FAQ from "./features/Public/RawData/FAQ";
import Version from "./pages/version/Version";
import ContactUsHome from "./features/ContactUsHome/ContactUsHome";
import EmployerPublicSubscription from "./pages/EmployerPublicSubscription/EmployerPublicSubscription";

// Auth
import LoginPage from "./pages/CommonPublicPages/LoginSignupPage/LoginPage";
import SignupPage from "./pages/CommonPublicPages/LoginSignupPage/SignupPage";
import HomeSignup from "./features/auth/components/HomeSignup";
import PhoneVerification from "./features/auth/components/PhoneVerif";
import EmailForm from "./features/auth/components/PasswordResetRequest";
import ResetPasswordForm from "./features/auth/components/PasswordReset";
import UpdatePasswordForm from "./features/auth/components/ChangePassword";
import VerifyEmailButton from "./features/auth/components/EmailVerification";
import VerifiedPage from "./features/auth/components/EmailVerifyConfirmation";
import PhoneVerifCandidate from "./features/login/PhoneVerifCandidate";
import CandRegVerif from "./features/login/CandRegVerif";
import { ConfirmPasswordReset } from "./features/auth/Slice/PasswordReset/PasswordResetAPI";
import { PasswordResetRequest } from "./features/auth/Slice/PasswordResetRequest/PasswordResetRequestAPI";

// Jobs & Companies
import { JobSingle } from "./features/jobApply/JobSingle";
import JobSearch2 from "./features/jobSearch/jobSearch2";
import { JobByRolesPage } from "./features/jobList/PopularJobRolesList/JobRolesPage";
import { JobByCategoriesPage } from "./features/jobList/PopularJobCategoriesList/JobCategoriesPage";

// Employer Dashboard shell
import { EdashPage } from "./pages/Dashboards/EdashboardPage";
import CompanyProfile from "./features/employerDashboard/components/ProfileTesting";
import InterviewScheduling from "./features/employerDashboard/components/InterviewScheduling";
import ManageJobsPage from "./pages/Employers/EmpDash/ManageJobsPage";
import { JobSingle2Page } from "./pages/Employers/EmpDash/JobSinglePage2";
import SubscriptionPlan from "./features/employerDashboard/components/SubscriptionPlan";
import SubscriptionDashboard from "./features/employerDashboard/employerDashboardSlice/PaymentGateway/EmployerSubscriptionHistory";
import CandidateSearchForm from "./features/employerDashboard/components/SearchCandidate2";
import ApplicantCard from "./features/employerDashboard/components/ApplicantCard";
import FeedBack from "./features/employerDashboard/components/Feedback";
import HelpCenter from "./features/employerDashboard/components/Support";
import UnlockedCandidate from "./features/employerDashboard/components/UnlockedCandidate";
import SavedCandidates from "./features/employerDashboard/components/SavedCandidates";
import ShowCandidate from "./features/employerDashboard/components/Candidate/CandidateSlice/ShowCandidate/ShowCandidate";
import ApplicantListByJob from "./features/employerDashboard/employerDashboardSlice/applicantListByJob/applicantListByJob";
import PaymentInitiate from "./features/employerDashboard/employerDashboardSlice/PaymentGateway/PaymentInitiate";
import PaymentInitiateTopUp from "./features/employerDashboard/employerDashboardSlice/PaymentGateway/PaymentInitiateTopUp";

// Employer job post flow
import JobDetailsPage1 from "./pages/Employers/jobPost/JobDetailsPage1";
import JobDetails1 from "./features/employers/components/JobDetails1";
import JobDetails2 from "./features/employers/components/jobDetails2";
import { JobReview } from "./features/employers/components/jobReview";

// Employer candidate tabs
import CandidatePage from "./pages/Employers/EmpDash/CandidatePage";
import Active from "./features/employerDashboard/components/Candidate/components/Active";
import Shortlisted from "./features/employerDashboard/components/Candidate/components/Shortlisted";
import AwaitingReview from "./features/employerDashboard/components/Candidate/components/AwaitingReview";
import InterviewScheduled from "./features/employerDashboard/components/Candidate/components/Reviewed";
import Contacting from "./features/employerDashboard/components/Candidate/components/Contacting";
import Rejected from "./features/employerDashboard/components/Candidate/components/Rejected";
import Hired from "./features/employerDashboard/components/Candidate/components/Hired";

// Employer profile creation
import EmpRegVerif from "./features/employers/components/empRegVerif";
import { EmployerBasicsPage } from "./pages/Employers/EmployerProfileCreation/EmployerBasicsPage";
import CompVerifPage from "./pages/Employers/EmployerProfileCreation/CompVerifPage";
import ComapanyVerif2 from "./features/CompVerif/CompVerif2";

// Admin Dashboard
import AdminDashboard from "./pages/Dashboards/AdminDashboard";
import AdminProfile from "./features/AdminPanel/AdminDashboard/components/AdminProfile";
import { JobPosting } from "./features/AdminPanel/AdminDashboard/components/JobPostings";
import AllSubscriptions from "./features/AdminPanel/AdminDashboard/components/AllSubscriptions";
import CoreIndustry from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/CoreIndustry";
import JobTitlesList from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobTitlesList";
import JobTypesList from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobTypesList";
import JobDepartments from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobDepartments";
import JobEducationCourse from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobEducationCourse";
import JobEducationCourseSpecialty from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobEducationCourseSpecialty";
import JobCompanyList from "./features/AdminPanel/AdminDashboard/components/JobCompanyList";
import { AdminJobList } from "./features/AdminPanel/JobList/AdminJobList";
import CityList from "./features/AdminPanel/AdminDashboard/components/CityList";
import Testing from "./features/AdminPanel/Testimonials/Testing";
import JobPostLocations from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobPostLocations";
import { JobShifts } from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobShifts";
import JobTimeline from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/JobTimeline";
import MinmQualif from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/MinmQualif";
import TotalExperience from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/TotalExperience";
import ReqdSkills from "./features/AdminPanel/AdminDashboard/components/JobPostingComp/ReqdSkills";
import CandidateList from "./features/AdminPanel/AdminDashboard/components/CandidateUserList";
import EmployerList from "./features/AdminPanel/AdminDashboard/components/EmployerUserList";
import SubscriberList from "./features/AdminPanel/AdminDashboard/components/SubscriberList";
import ViewSubscriptions from "./features/AdminPanel/AdminDashboard/components/ViewSubscriptions";
import AdminReviews from "./features/AdminPanel/AdminDashboard/components/AdminReviews";

// Misc
// import DummyPage from "./pages/CommonPublicPages/Navbars/components/Dummy";
import DummyPage from "./pages/CommonPublicPages/Navbars/components/DummyPage"
import HoverDropdown from "./features/AdminPanel/JobPostingSlice/ExampleList";
import ExampleList2 from "./features/AdminPanel/JobPostingSlice/ExampleList2";
import { ExampleReview } from "./features/AdminPanel/JobPostingSlice/ExampleReview";
import { ThankYou } from "./features/notif&msg/thankYou";
import PhoneVerificationPhone from "./features/auth/components/PhoneVerificationPhone";
import TermsOfService from "./features/Public/RawData/TermsOfService";

const RouterErrorFallback = () => {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f8f9fa",
      padding: "20px",
      fontFamily: "Arial, sans-serif"
    }}>
      <div style={{
        textAlign: "center",
        padding: "40px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        maxWidth: "600px",
        width: "100%",
        border: "1px solid #e9ecef"
      }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔄</div>
        <h1 style={{
          fontSize: "28px",
          fontWeight: "bold",
          color: "#dc3545",
          marginBottom: "16px"
        }}>
          Navigation Error
        </h1>
        <p style={{
          fontSize: "16px",
          marginBottom: "24px",
          color: "#6c757d",
          lineHeight: "1.5"
        }}>
          There was an error with page navigation. Please try refreshing or go back to the home page.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "500",
              minWidth: "120px"
            }}
          >
            Reload Page
          </button>
          <button
            onClick={() => window.location.href = "/"}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "500",
              minWidth: "120px"
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <DummyPage />,
    errorElement: <RouterErrorFallback />,
    children: [
      {
        index: true,
        element: (
          <GlobalErrorBoundary>
            <HomePage />
          </GlobalErrorBoundary>
        ),
      },
      {
        path: "/company-list",
        element: <GlobalErrorBoundary><CompanyListPage /></GlobalErrorBoundary>,
      },
      {
        path: "/company-list/:uuid",
        element: <GlobalErrorBoundary><CompanySinglePage /></GlobalErrorBoundary>,
      },
      {
        path: "/job-desc",
        element: <GlobalErrorBoundary><JobSingle /></GlobalErrorBoundary>,
      },
      {
        path: "/jobs-roles/:uuid",
        element: <GlobalErrorBoundary><JobByRolesPage /></GlobalErrorBoundary>,
      },
      {
        path: "/job-categories/:uuid",
        element: <GlobalErrorBoundary><JobByCategoriesPage /></GlobalErrorBoundary>,
      },
    ],
  },

  // Employer Dashboard
  {
    path: "/employer-dashboard/",
    element: (
      <GlobalErrorBoundary>
        <ProtectedRoute allow={["employer"]}>
          <EdashPage />
        </ProtectedRoute>
      </GlobalErrorBoundary>
    ),
    errorElement: <RouterErrorFallback />,
    children: [
      { index: true, element: <GlobalErrorBoundary><CompanyProfile /></GlobalErrorBoundary> },
      { path: "schedule-interview", element: <GlobalErrorBoundary><InterviewScheduling /></GlobalErrorBoundary> },
      { path: "manage-jobs", element: <GlobalErrorBoundary><ManageJobsPage /></GlobalErrorBoundary> },
      { path: "manage-jobs-details/:uuid", element: <GlobalErrorBoundary><JobSingle2Page /></GlobalErrorBoundary> },
      // { path: "subscription-plan", element: <GlobalErrorBoundary><SubscriptionPlan /></GlobalErrorBoundary> },
      // { path: "subscription-billings", element: <GlobalErrorBoundary><SubscriptionDashboard /></GlobalErrorBoundary> },
      { path: "search-candidate", element: <GlobalErrorBoundary><CandidateSearchForm /></GlobalErrorBoundary> },
      { path: "candidate-profile-list", element: <GlobalErrorBoundary><ApplicantCard /></GlobalErrorBoundary> },
      { path: "edit-feedback", element: <GlobalErrorBoundary><FeedBack /></GlobalErrorBoundary> },
      { path: "support", element: <GlobalErrorBoundary><HelpCenter /></GlobalErrorBoundary> },
      { path: "unlocked-candidate", element: <GlobalErrorBoundary><UnlockedCandidate /></GlobalErrorBoundary> },
      { path: "save-candidate", element: <GlobalErrorBoundary><SavedCandidates /></GlobalErrorBoundary> },
      {
        path: "change-password",
        element: (
          <GlobalErrorBoundary>
            <ProtectedRoute role="employer">
              <UpdatePasswordForm />
            </ProtectedRoute>
          </GlobalErrorBoundary>
        ),
      },
      { path: "show-candidate/:uuid", element: <GlobalErrorBoundary><ShowCandidate /></GlobalErrorBoundary> },
      {
        path: "verify-email",
        element: (
          <GlobalErrorBoundary>
            <ProtectedRoute role="employer">
              <VerifyEmailButton />
            </ProtectedRoute>
          </GlobalErrorBoundary>
        ),
      },

      { path: "applicants/:jobId", element: <GlobalErrorBoundary><ApplicantListByJob /></GlobalErrorBoundary> },
      {
        path: "post-job",
        element: <GlobalErrorBoundary><JobDetailsPage1 /></GlobalErrorBoundary>,
        children: [
          { index: true, element: <GlobalErrorBoundary><JobDetails1 /></GlobalErrorBoundary> },
          { path: "additional", element: <GlobalErrorBoundary><JobDetails2 /></GlobalErrorBoundary> },
          { path: "review", element: <GlobalErrorBoundary><JobReview /></GlobalErrorBoundary> },
        ],
      },
      {
        path: "candidate/",
        element: <GlobalErrorBoundary><CandidatePage /></GlobalErrorBoundary>,
        children: [
          { index: true, element: <GlobalErrorBoundary><Active /></GlobalErrorBoundary> },
          { path: "shortlisted", element: <GlobalErrorBoundary><Shortlisted /></GlobalErrorBoundary> },
          { path: "awaiting-review", element: <GlobalErrorBoundary><AwaitingReview /></GlobalErrorBoundary> },
          { path: "scheduled", element: <GlobalErrorBoundary><InterviewScheduled /></GlobalErrorBoundary> },
          { path: "contacting", element: <GlobalErrorBoundary><Contacting /></GlobalErrorBoundary> },
          { path: "rejected", element: <GlobalErrorBoundary><Rejected /></GlobalErrorBoundary> },
          { path: "hired", element: <GlobalErrorBoundary><Hired /></GlobalErrorBoundary> },
        ],
      },
      { path: "subscription-billing", element: <GlobalErrorBoundary><SubscriptionBillingPage /></GlobalErrorBoundary> },
    ],
  },

  {
    path: "verify-email/:token",
    element: (
      <GlobalErrorBoundary>

        <VerifiedPage />

      </GlobalErrorBoundary>
    ),
  },

  // Candidate home when logged in
  {
    path: "/home2",
    element: (
      <GlobalErrorBoundary>
        <ProtectedRoute role="candidate">
          <HomeLoggedIn />
        </ProtectedRoute>
      </GlobalErrorBoundary>
    ),
  },

  // Auth and misc
  { path: "reset-code-request", element: <GlobalErrorBoundary><EmailForm /></GlobalErrorBoundary> },
  { path: "password-reset", element: <GlobalErrorBoundary><ResetPasswordForm /></GlobalErrorBoundary> },
  { path: "/login", element: <GlobalErrorBoundary><LoginPage /></GlobalErrorBoundary> },
  { path: "/version", element: <GlobalErrorBoundary><Version /></GlobalErrorBoundary> },
  { path: "/testing", element: <GlobalErrorBoundary><Testing /></GlobalErrorBoundary> },
  { path: "/jobSearch2", element: <GlobalErrorBoundary><JobSearch2 /></GlobalErrorBoundary> },

  // Admin Dashboard
  {
    path: "/admin-dashboard/",
    element: (
      <GlobalErrorBoundary>
        <ProtectedRoute allow={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      </GlobalErrorBoundary>
    ),
    children: [
      { index: true, element: <GlobalErrorBoundary><AdminProfile /></GlobalErrorBoundary> },
      { path: "jobPosting", element: <GlobalErrorBoundary><JobPosting /></GlobalErrorBoundary> },
      { path: "createplan", element: <GlobalErrorBoundary><AllSubscriptions /></GlobalErrorBoundary> },
      { path: "core-industry", element: <GlobalErrorBoundary><CoreIndustry /></GlobalErrorBoundary> },
      { path: "jobTitles", element: <GlobalErrorBoundary><JobTitlesList /></GlobalErrorBoundary> },
      { path: "jobTypes", element: <GlobalErrorBoundary><JobTypesList /></GlobalErrorBoundary> },
      { path: "jobDepartments", element: <GlobalErrorBoundary><JobDepartments /></GlobalErrorBoundary> },
      { path: "jobEducationCourse", element: <GlobalErrorBoundary><JobEducationCourse /></GlobalErrorBoundary> },
      { path: "jobEducationCourseSpecialty", element: <GlobalErrorBoundary><JobEducationCourseSpecialty /></GlobalErrorBoundary> },
      { path: "companyList", element: <GlobalErrorBoundary><JobCompanyList /></GlobalErrorBoundary> },
      { path: "adminJobs", element: <GlobalErrorBoundary><AdminJobList /></GlobalErrorBoundary> },
      { path: "cityList", element: <GlobalErrorBoundary><CityList /></GlobalErrorBoundary> },
      { path: "testimonials", element: <GlobalErrorBoundary><Testing /></GlobalErrorBoundary> },
      { path: "jobPostLocations", element: <GlobalErrorBoundary><JobPostLocations /></GlobalErrorBoundary> },
      { path: "jobShifts", element: <GlobalErrorBoundary><JobShifts /></GlobalErrorBoundary> },
      { path: "jobTimeline", element: <GlobalErrorBoundary><JobTimeline /></GlobalErrorBoundary> },
      { path: "jobMinmQualif", element: <GlobalErrorBoundary><MinmQualif /></GlobalErrorBoundary> },
      { path: "jobEducation", element: <GlobalErrorBoundary><MinmQualif /></GlobalErrorBoundary> },
      { path: "totalExpMinm", element: <GlobalErrorBoundary><TotalExperience /></GlobalErrorBoundary> },
      { path: "reqdSkills", element: <GlobalErrorBoundary><ReqdSkills /></GlobalErrorBoundary> },
      { path: "candidateProfile", element: <GlobalErrorBoundary><CandidateList /></GlobalErrorBoundary> },
      { path: "employersProfile", element: <GlobalErrorBoundary><EmployerList /></GlobalErrorBoundary> },
      { path: "subscriber-list", element: <GlobalErrorBoundary><SubscriberList /></GlobalErrorBoundary> },
      { path: "viewSubscriptions", element: <GlobalErrorBoundary><ViewSubscriptions /></GlobalErrorBoundary> },
      { path: "reviews", element: <GlobalErrorBoundary><AdminReviews /></GlobalErrorBoundary> },
    ],
  },

  {
    path: "/employer-basics",
    element: (
      <GlobalErrorBoundary>
        <ProtectedRoute role="employer">
          <EmployerBasicsPage />
        </ProtectedRoute>
      </GlobalErrorBoundary>
    ),
  },
  { path: "/signup", element: <GlobalErrorBoundary><SignupPage /></GlobalErrorBoundary> },
  { path: "/homesignup", element: <GlobalErrorBoundary><HomeSignup /></GlobalErrorBoundary> },
  { path: "/example2", element: <GlobalErrorBoundary><ExampleList2 /></GlobalErrorBoundary> },
  { path: "/about-us", element: <GlobalErrorBoundary><AboutUs /></GlobalErrorBoundary> },
  { path: "/core-values", element: <GlobalErrorBoundary><CoreValues /></GlobalErrorBoundary> },
  { path: "/privacy-policy", element: <GlobalErrorBoundary><PrivacyPolicy /></GlobalErrorBoundary> },
  {
    path: "/terms",
    element: (
      <GlobalErrorBoundary>
        <TermsOfService />
      </GlobalErrorBoundary>
    ),
  },
  { path: "/refund-policy", element: <GlobalErrorBoundary><RefundPolicy /></GlobalErrorBoundary> },
  { path: "/contact-us", element: <GlobalErrorBoundary><ContactUsHome /></GlobalErrorBoundary> },
  { path: "/faq", element: <GlobalErrorBoundary><FAQ /></GlobalErrorBoundary> },
  // { path: "/pricing", element: <GlobalErrorBoundary><EmployerPublicSubscription /></GlobalErrorBoundary> },
  { path: "/example", element: <GlobalErrorBoundary><HoverDropdown /></GlobalErrorBoundary> },
  { path: "/phone-verification", element: <GlobalErrorBoundary><PhoneVerification /></GlobalErrorBoundary> },
  { path: "/phone-verification-phone", element: <GlobalErrorBoundary><PhoneVerificationPhone /></GlobalErrorBoundary> },
  { path: "/notify", element: <GlobalErrorBoundary><Notify /></GlobalErrorBoundary> },
  { path: "/phoneverifcandidate", element: <GlobalErrorBoundary><PhoneVerifCandidate /></GlobalErrorBoundary> },
  { path: "/candregverif", element: <GlobalErrorBoundary><CandRegVerif /></GlobalErrorBoundary> },
  { path: "/myreviews", element: <GlobalErrorBoundary><MyReviewsPage /></GlobalErrorBoundary> },
  { path: "/success-page", element: <GlobalErrorBoundary><PaymentInitiate /></GlobalErrorBoundary> },
  { path: "/success-page-topup", element: <GlobalErrorBoundary><PaymentInitiateTopUp /></GlobalErrorBoundary> },
  { path: "/empRegVerif", element: <GlobalErrorBoundary><EmpRegVerif /></GlobalErrorBoundary> },
  { path: "/thanku", element: <GlobalErrorBoundary><ThankYou /></GlobalErrorBoundary> },
  { path: "/compVerif", element: <GlobalErrorBoundary><CompVerifPage /></GlobalErrorBoundary> },
  { path: "/compVerif2", element: <GlobalErrorBoundary><ComapanyVerif2 /></GlobalErrorBoundary> },
  { path: "*", element: <GlobalErrorBoundary><NotFound404 /></GlobalErrorBoundary> },
]);

// Global handlers
window.addEventListener("unhandledrejection", (event) => {
  console.warn("Suppressed Promise Rejection:", event.reason);
  event.preventDefault();
});
window.addEventListener("error", (event) => {
  console.warn("Suppressed Error:", event.message);
  event.preventDefault();
});

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchTokenAsync());
  }, [dispatch]);

  return (
    <div className="App">
      <ChakraProvider>
        <GlobalErrorBoundary>
          <RouterProvider router={router} />
        </GlobalErrorBoundary>
      </ChakraProvider>
    </div>
  );
}

export default App;
